import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { DealerApiWrapper, modalStyle } from "../style/DealerApiWrapper";
import { formatDate, toastError, toastSuccess } from "../helpers";

const QUOTAS = [
  { key: "daily_listings", label: "Listings per day", hint: "Listings accepted by its pushes (a push that would pass it is refused whole)." },
  { key: "daily_photos", label: "Photos per day", hint: "Photos downloaded from its URLs or uploaded (past it, photos wait for a later push)." },
];

// API value → editor state: null = platform default, 0 = unlimited, n = own limit.
const toEditor = (value) =>
  value === null || value === undefined ? { mode: "default", limit: "" } : value === 0 ? { mode: "unlimited", limit: "" } : { mode: "limit", limit: String(value) };

const fromEditor = ({ mode, limit }) => (mode === "default" ? null : mode === "unlimited" ? 0 : parseInt(limit, 10));

const usage = (q) => (q ? `${q.used.toLocaleString()} used today of ${q.limit === null ? "unlimited" : q.limit.toLocaleString()}` : "-");

/**
 * A dealership's daily quotas (PUT /access/{subsidiary}/quotas). The platform default is
 * unlimited; usage is counted either way and resets at midnight Amsterdam time (CET in winter, CEST in summer).
 */
const QuotaModal = ({ subsidiary, onClose, onSaved }) => {
  const open = subsidiary !== null;
  const [loading, _loading] = useState(false);
  const [busy, _busy] = useState(false);
  const [current, _current] = useState(null); // status.quotas
  const [editor, _editor] = useState({});
  const [note, _note] = useState("");
  const [updatedAt, _updatedAt] = useState(null);

  // Guarded against a slow response from a dealership the admin has already moved on from: it
  // would otherwise fill the editor with that dealership's limits under this one's name, and the
  // next Save would write them to the wrong dealership.
  const seq = useRef(0);

  useEffect(() => {
    const current = ++seq.current;

    if (!open) return;

    _loading(true);
    _current(null);
    _editor({});
    _note("");
    _updatedAt(null);
    API.get(`${APP_URLS.DEALER_API}/access/${subsidiary.id}/status`)
      .then((res) => {
        if (current !== seq.current) return;
        const resp = res.data;
        if (resp.success !== true) {
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
          return;
        }
        const override = resp.data.quota_override;
        _current(resp.data.quotas);
        _editor({ daily_listings: toEditor(override?.daily_listings), daily_photos: toEditor(override?.daily_photos) });
        _note(override?.note ?? "");
        _updatedAt(override?.updated_at ?? null);
      })
      .catch((error) => current === seq.current && toastError(error))
      .finally(() => current === seq.current && _loading(false));
  }, [open, subsidiary]);

  const setQuota = (key, patch) => _editor((e) => ({ ...e, [key]: { ...e[key], ...patch } }));

  const save = () => {
    for (const q of QUOTAS) {
      const { mode, limit } = editor[q.key];
      if (mode === "limit" && !/^[1-9]\d{0,7}$/.test(String(limit).trim())) {
        toast.error(`${q.label}: enter a whole number above 0.`, { position: toast.POSITION.TOP_RIGHT });
        return;
      }
    }

    _busy(true);
    API.put(`${APP_URLS.DEALER_API}/access/${subsidiary.id}/quotas`, {
      daily_listings: fromEditor(editor.daily_listings),
      daily_photos: fromEditor(editor.daily_photos),
      note: note.trim() === "" ? null : note.trim(),
    })
      .then((res) => {
        if (res.data.success === true) {
          toastSuccess(res.data.message);
          onSaved();
          onClose();
        } else {
          toast.error(res.data.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(toastError)
      .finally(() => _busy(false));
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()}>
      <Box sx={modalStyle}>
        <DealerApiWrapper>
          <h4 className="mb-1">Daily quotas{subsidiary ? ` — ${subsidiary.name}` : ""}</h4>
          <p className="muted">
            The platform default is unlimited. Counters reset at midnight Amsterdam time; a change applies to the next
            push.
          </p>

          {loading || !current ? (
            <div className="d-flex justify-content-center">
              {loading ? (
                <ThreeDots height="80" width="80" radius="9" color="#637df4" visible />
              ) : (
                <p className="muted">This dealership&apos;s quotas could not be loaded.</p>
              )}
            </div>
          ) : (
            <>
              {QUOTAS.map((q) => (
                <div key={q.key} className="mb-3 text-start">
                  <div className="fw-bold">{q.label}</div>
                  <div className="muted mb-1">
                    {q.hint} Now: {usage(current[q.key])} ({current[q.key]?.source === "dealership" ? "own value" : "platform default"}).
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    {[
                      ["default", "Platform default"],
                      ["unlimited", "Unlimited"],
                      ["limit", "Limit"],
                    ].map(([mode, label]) => (
                      <label key={mode} className="d-flex align-items-center gap-1 mb-0">
                        <input
                          type="radio"
                          name={`quota-${q.key}`}
                          checked={editor[q.key]?.mode === mode}
                          onChange={() => setQuota(q.key, { mode })}
                        />
                        {label}
                      </label>
                    ))}
                    <input
                      type="number"
                      min={1}
                      className="filter-input"
                      disabled={editor[q.key]?.mode !== "limit"}
                      value={editor[q.key]?.limit ?? ""}
                      onChange={(e) => setQuota(q.key, { limit: e.target.value })}
                    />
                  </div>
                </div>
              ))}

              <label className="form-label fw-bold" htmlFor="dealer-api-quota-note">
                Note <span className="muted">(optional, for support staff only)</span>
              </label>
              <textarea
                id="dealer-api-quota-note"
                className="form-control"
                rows={2}
                maxLength={500}
                value={note}
                onChange={(e) => _note(e.target.value)}
              />
              {updatedAt && <div className="muted mt-1">Last changed {formatDate(updatedAt)}</div>}

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="outlined" disabled={busy} onClick={onClose} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button variant="contained" disabled={busy} onClick={save} sx={{ textTransform: "none", fontWeight: 600 }}>
                  Save quotas
                </Button>
              </div>
            </>
          )}
        </DealerApiWrapper>
      </Box>
    </Modal>
  );
};

export default QuotaModal;
